import React, { useRef, useEffect, useState } from "react";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import "mapbox-gl/dist/mapbox-gl.css";
import {ReactSession} from "../../utils/ReactSession.jsx";
import {AES, enc} from "crypto-js";
import ApiRequester from "../ApiRequester.jsx";
import {handleSubmitActions, formSuccess, formFail} from "../../libs.jsx";
import Button from "./Button.jsx";

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

const GeofenceTool = (props) => {

    let bData, dData;

    if (ReactSession.get("PAL") != null){
        bData = AES.decrypt(ReactSession.get("PAL"), import.meta.env.VITE_ESECRET);
        dData = JSON.parse(bData.toString(enc.Utf8));
    }
    
    const mapContainer = useRef(null);
    const map = useRef(null);
    const draw = useRef(null);
    const resizeObserver = useRef(null);
    const [lng, setLng] = useState(props?.latitude ? props.latitude : -87.9096);
    const [lat, setLat] = useState(props?.longitude ? props.longitude : 43.0339);
    const [zoom, setZoom] = useState(17);
    const [features, setFeatures] = useState([]);
    const [activeFeatureId, setActiveFeatureId] = useState(null);
    const [mapInitialized, setMapInitialized] = useState(false);
    const [selectedFeature, setSelectedFeature] = useState(null);
    const [rotationAngle, setRotationAngle] = useState(0);
    const [showHelp, setShowHelp] = useState(false);
    const [zoneNames, setZoneNames] = useState({}); // Store zone names by feature ID
    const [zoneLayers, setZoneLayers] = useState({}); // Store zone layers by feature ID
    const [zoneTypes, setZoneTypes] = useState({}); // Store zone types by feature ID
    const [editingZoneId, setEditingZoneId] = useState(null); // Track which zone name is being edited
    const [editingText, setEditingText] = useState(""); // New state to track the current editing value
    const [address, setAddress] = useState("");
    const [searchError, setSearchError] = useState("");
    const [backendData, setBackendData] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [loadData, setLoadData] = useState(false);
    const [selectedZoneType, setSelectedZoneType] = useState(0);

    // Initialize selectedZoneType with first value from zoneTypes
    useEffect(() => {
        if (props.zoneTypes && props.zoneTypes.length > 0) {
            setSelectedZoneType(props.zoneTypes[0].zone_type_id);
        }
    }, [props.zoneTypes]);

    // Effect for initializing the map and setting up ResizeObserver
    useEffect(() => {
        // Prevent re-initialization
        if (map.current || !mapContainer.current) {
            return;
        }

        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: "mapbox://styles/mapbox/streets-v12",
            center: [props?.longitude, props.latitude],
            zoom: zoom,
            trackResize: true // Keep mapbox internal resize handling enabled as well
        });

        const currentMap = map.current; // Capture instance

        // --- Setup ResizeObserver ---
        resizeObserver.current = new ResizeObserver(() => {
            // Use requestAnimationFrame to debounce and prevent errors during rapid resize
            window.requestAnimationFrame(() => {
                if (map.current) { // Check if map instance still exists
                   map.current.resize();
                }
            });
        });
        // Start observing the map container element
        resizeObserver.current.observe(mapContainer.current);
        // --- End ResizeObserver Setup ---

        currentMap.on("load", () => {
            // Add navigation control
            const nav = new mapboxgl.NavigationControl();
            currentMap.addControl(nav, "top-right");

            // Add drawing tools
            if (!draw.current) {
                draw.current = new MapboxDraw({
                    displayControlsDefault: true,
                    controls: { 
                        trash: true,
                        point: false,
                        line_string: false,
                        polygon: false,
                        combine_features: false,
                        uncombine_features: false,
                        vertex: false
                    },
                    styles: [ /* Your Draw styles here */
                        {
                            id: "gl-draw-polygon-fill-inactive", type: "fill",
                            filter: ["all", ["==", "active", "false"], ["==", "$type", "Polygon"]],
                            paint: { "fill-color": "#3bb2d0", "fill-outline-color": "#3bb2d0", "fill-opacity": 0.5 },
                        },
                        {
                            id: "gl-draw-polygon-fill-active", type: "fill",
                            filter: ["all", ["==", "active", "true"], ["==", "$type", "Polygon"]],
                            paint: { "fill-color": "#fbb03b", "fill-outline-color": "#fbb03b", "fill-opacity": 0.5 },
                        },
                        {
                            id: "gl-draw-polygon-stroke-inactive", type: "line",
                            filter: ["all", ["==", "active", "false"], ["==", "$type", "Polygon"]],
                            layout: { "line-cap": "round", "line-join": "round" },
                            paint: { "line-color": "#3bb2d0", "line-width": 2 },
                        },
                        {
                            id: "gl-draw-polygon-stroke-active", type: "line",
                            filter: ["all", ["==", "active", "true"], ["==", "$type", "Polygon"]],
                            layout: { "line-cap": "round", "line-join": "round" },
                            paint: { "line-color": "#fbb03b", "line-dasharray": [0.2, 2], "line-width": 2 },
                        },
                        {
                            id: "gl-draw-vertex-inactive", type: "circle",
                            filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"]],
                            paint: { 
                                "circle-radius": 10, 
                                "circle-color": "#fff", 
                                "circle-stroke-color": "#3bb2d0", 
                                "circle-stroke-width": 3,
                                "circle-opacity": 1
                            },
                        },
                        {
                            id: "gl-draw-vertex-active", type: "circle",
                            filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"], ["==", "active", "true"]],
                            paint: { 
                                "circle-radius": 12, 
                                "circle-color": "#fff", 
                                "circle-stroke-color": "#fbb03b", 
                                "circle-stroke-width": 3,
                                "circle-opacity": 1
                            },
                        }
                    ],
                });

                currentMap.addControl(draw.current);
                currentMap.on("draw.create", handleDrawCreate);
                currentMap.on("draw.update", handleDrawUpdate);
                currentMap.on("draw.delete", handleDrawDelete);
                currentMap.on("draw.selectionchange", handleSelectionChange);
            }
            setMapInitialized(true);
        });

        currentMap.on('error', (e) => {
            console.error("Mapbox Error:", e);
        });

        // Cleanup function
        return () => {

            // Disconnect the observer
            if (resizeObserver.current) {
                resizeObserver.current.disconnect();
                resizeObserver.current = null; // Clear the ref
            }

            // Remove the map instance
            if (currentMap) {
                currentMap.remove();
            }
             map.current = null; // Clear the map ref
             draw.current = null; // Clear draw ref
             setMapInitialized(false); // Reset state
        };
    }, []);

    const handleDrawCreate = (e) => {
        updateFeatures();
        setLoadData(true);
    };

    // Update features when drawings are updated
    const handleDrawUpdate = (e) => {
        // Update features
        updateFeatures();
        setLoadData(true);
    };

    // Update features when drawings are deleted
    const handleDrawDelete = (e) => {
        updateFeatures();
        setLoadData(true);
    };

    // Update features state
    const updateFeatures = () => {
        const data = draw.current.getAll();
        setFeatures(data.features);

        // Update state from feature properties
        data.features.forEach((feature) => {
            if (feature.properties) {
                if (feature.properties.zone_name) {
                    setZoneNames(prev => ({...prev, [feature.id]: feature.properties.zone_name}));
                }
                if (feature.properties.layer !== undefined) {
                    setZoneLayers(prev => ({...prev, [feature.id]: feature.properties.layer}));
                }
                if (feature.properties.zone_type_id !== undefined) {
                    setZoneTypes(prev => ({...prev, [feature.id]: feature.properties.zone_type_id}));
                }
            }
        });
    };

    // Handle selection change to know which feature is active
    const handleSelectionChange = (e) => {
        if (e.features.length > 0) {
            const selectedId = e.features[0].id;
            setActiveFeatureId(selectedId);
            setSelectedFeature(e.features[0]);
        } else {
            setActiveFeatureId(null);
            setSelectedFeature(null);
        }
    };

    // Add square to the map
    const addSquare = () => {
        if (!draw.current) return;

        // Default square around the center point
        const center = map.current.getCenter();
        const offset = 0.0005; // Adjust this value to change the size of the square

        const coordinates = [
            [center.lng - offset, center.lat - offset],
            [center.lng + offset, center.lat - offset],
            [center.lng + offset, center.lat + offset],
            [center.lng - offset, center.lat + offset],
            [center.lng - offset, center.lat - offset] // Close the polygon
        ];

        const feature = {
            type: "Feature",
            properties: {
                shape: "square",
                zone_type_id: selectedZoneType,
                zone_name: "Building Perim",
                layer: 0
            },
            geometry: {
                type: "Polygon",
                coordinates: [coordinates]
            }
        };

        // Add feature to map
        draw.current.add(feature);
        updateFeatures();
        setLoadData(true);
    };

    // Rotate the selected feature
    const rotateFeature = (angle) => {
        if (!draw?.current || !activeFeatureId){
            return;
        }

        const selectedFeature = draw.current?.get(activeFeatureId);
        if (!selectedFeature){
            return;
        }

        const newAngle = rotationAngle + angle;
        setRotationAngle(newAngle);

        // Get the center of the feature to rotate around
        const coordinates = selectedFeature.geometry.coordinates[0];
        const center = calculateCenter(coordinates);

        // Apply rotation to each coordinate
        const rotatedCoordinates = coordinates.map((coord) => {
            return rotatePoint(coord, center, angle);
        });

        // Update the feature with rotated coordinates
        selectedFeature.geometry.coordinates[0] = rotatedCoordinates;
        draw.current.add(selectedFeature);

        // Make sure features and hierarchy are updated after rotation
        setTimeout(() => {
            updateFeatures();
        }, 100);
    };

    // Calculate center of a polygon
    const calculateCenter = (coordinates) => {
        let sumX = 0;
        let sumY = 0;
        // Skip the last coordinate which closes the polygon
        const points = coordinates.slice(0, -1);
        for (const point of points) {
            sumX += point[0];
            sumY += point[1];
        }
        return [sumX / points.length, sumY / points.length];
    };

    // Rotate a point around a center by an angle (in degrees)
    const rotatePoint = (point, center, angleDeg) => {
        const angleRad = (angleDeg * Math.PI) / 180;
        // Translate point to origin
        const x = point[0] - center[0];
        const y = point[1] - center[1];
        // Rotate point
        const xNew = x * Math.cos(angleRad) - y * Math.sin(angleRad);
        const yNew = x * Math.sin(angleRad) + y * Math.cos(angleRad);
        // Translate point back
        return [xNew + center[0], yNew + center[1]];
    };

    // Duplicate the selected feature
    const duplicateFeature = () => {
        if (!draw?.current || !activeFeatureId){
            return;
        }

        const feature = draw.current?.get(activeFeatureId);
        if (!feature){
            return;
        }

        // Create a copy with slight offset
        const newFeature = JSON.parse(JSON.stringify(feature));
        delete newFeature.id; // Remove ID so a new one is generated

        // Calculate an appropriate offset based on the current zoom level
        const currentZoom = map.current.getZoom();
        let offset = 0.005; // Default offset

        if (currentZoom >= 16) {
            // For building-level zoom, use a much smaller offset
            offset = 0.0003;
        } else if (currentZoom >= 13) {
            offset = 0.001;
        } else if (currentZoom >= 10) {
            offset = 0.002;
        }

        // Offset the duplicate slightly
        newFeature.geometry.coordinates[0] = newFeature.geometry.coordinates[0].map((coord) => {
                return [coord[0] + offset, coord[1] + offset];
            }
        );

        draw.current.add(newFeature);

        // Update features and hierarchy
        setTimeout(() => {
            updateFeatures();
        }, 100);
    };

    // Geocode the address and move map to that location
    const geocodeAddress = () => {
        if (!address.trim()) {
            setSearchError("Please enter an address");
            return;
        }

        setSearchError("");

        // Use Mapbox Geocoding API
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${mapboxgl.accessToken}&limit=1`;

        fetch(url).then((response) => {
            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            return response.json();
            }).then((data) => {
                if (data.features && data.features.length > 0) {
                    const [lng, lat] = data.features[0].center;
                    map.current.flyTo({
                        center: [lng, lat],
                        zoom: 15,
                    });
                } else {
                    setSearchError("Address not found");
                }
            }).catch((error) => {
                console.error("Error geocoding address:", error);
                setSearchError("Error finding address. Please try again.");
        });
    };

    // Export data as JSON
    const exportJson = () => {
        if (!draw.current || features.length === 0){
            return;
        }

        const dataStr = JSON.stringify(prepareBackendData(), null, 2);

        // Create a download link
        const blob = new Blob([dataStr], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = "palcare-zones.json";
        link.href = url;
        link.click();
    };

    // Prepare data for backend in the table format
    const prepareBackendData = () => {
        if (!draw.current || features.length === 0) return;

        const retData = [];
        let pointIdCounter = 1;

        // Process each feature as a separate zone
        features.forEach((feature, index) => {
            const zoneName = zoneNames[feature.id] || `Zone ${index + 1}`;
            const layer = zoneLayers[feature.id] || 0;
            const zoneTypeId = zoneTypes[feature.id] || selectedZoneType;

            // Create zone object matching dataStruct format
            const zone = {
                zone_name: zoneName,
                zone_id: index + 1,
                layer: layer,
                points: [],
                zone_type_id: zoneTypeId
            };

            // Process coordinates as points
            const coordinates = feature.geometry.coordinates[0];
            // Remove the last coordinate (which closes the polygon and is the same as the first)
            const uniqueCoordinates = coordinates.slice(0, -1);

            // Only take the first 4 points to maintain square shape
            const squarePoints = uniqueCoordinates.slice(0, 4);
            squarePoints.forEach((coord) => {
                zone.points.push({
                    point_id: pointIdCounter++,
                    latitude: parseFloat(coord[1].toFixed(8)),
                    longitude: parseFloat(coord[0].toFixed(8))
                });
            });

            retData.push(zone);
        });

        return retData;
    };

    // Export data as CSV in table format
    const exportCsv = () => {
        if (!draw.current || features.length === 0) return;

        // Create CSV header
        let csvContent = "Campus Name,Campus ID,Zone ID,Zone Name,Layer,Zone Type ID,Point ID,Lat,Long\n";
        let expContent = prepareBackendData()
        expContent.forEach((row, index) => {
            row.points.forEach((point) => {
                csvContent += `${props.campus},${props.campusId},${row.zone_id},${row.zone_name},${row.layer},${row.zone_type_id},${point.point_id},${point.latitude},${point.longitude}\n`
            })
        });

        // Create a download link
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = "palcare-zones.csv";
        link.href = url;
        link.click();
    };

    // Get display information for a zone in the zone manager
    const getZoneDisplayInfo = (featureId, index) => {
        let displayName = zoneNames[featureId] || `Zone ${index + 1}`;
        return {displayName};
    };

    // Start editing a zone name
    const startEditing = (featureId) => {
        setEditingZoneId(featureId);
        setEditingText(zoneNames[featureId] || "Building Perim");
    };

    // Handle saving the zone name when editing is complete
    const handleSaveZoneName = (featureId) => {
        const finalName = editingText.trim() === "" ? "Zone" : editingText;
        setZoneNames((prev) => ({...prev, [featureId]: finalName}));
        
        // Update feature properties
        const feature = draw.current.get(featureId);
        if (feature) {
            feature.properties.zone_name = finalName;
            draw.current.add(feature);
        }
        
        setEditingZoneId(null);
    };

    // Center the map on a specific feature/zone
    const centerMapOnFeature = (featureId) => {

        if (!map?.current) {
            console.error("Map is not initialized");
            return;
        }

        if (!draw?.current) {
            console.error("Draw is not initialized");
            return;
        }

        // Try to get the feature directly from the draw object
        const feature = draw.current?.get(featureId);
        if (!feature) {
            console.error(`Feature ${featureId} not found in draw`);
            return;
        }


        try {
            // Get the coordinates and calculate center
            const coordinates = feature.geometry.coordinates[0];

            // Calculate center point manually
            let sumX = 0;
            let sumY = 0;
            let pointCount = 0;

            for (const point of coordinates) {
                sumX += point[0];
                sumY += point[1];
                pointCount++;
            }

            if (pointCount === 0) {
                console.error("No points found in the feature");
                return;
            }

            const centerX = sumX / pointCount;
            const centerY = sumY / pointCount;


            // Use flyTo for a smooth transition
            map.current.flyTo({
                center: [centerX, centerY],
                duration: 800, // Animation duration in milliseconds
                essential: true,
                easing: (t) => {
                    // Custom easing function for a natural feeling transition
                    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
                },
            });
        } catch (err) {
            console.error("Error centering map:", err);
        }
    };

    // When a zone item is clicked in the Zone Manager
    const handleZoneItemClick = (featureId) => {
        if (!draw.current || !map.current){
            return;
        }

        // Select the feature in the drawing tool
        draw.current.changeMode("simple_select", {featureIds: [featureId]});

        // Update state
        setActiveFeatureId(featureId);
        setSelectedFeature(features.find((f) => f.id === featureId));

        // Center the map on the selected feature with a slight delay
        // This helps ensure the map has fully processed the selection change
        setTimeout(() => {
            centerMapOnFeature(featureId);
        }, 100);
    };

    // SVG icons for edit and delete buttons
    const EditIcon = () => (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
        </svg>
    );

    useEffect(() => {
        if (loadData || showPreview){
            setBackendData(prepareBackendData());
            setLoadData(false);
        }
    }, [loadData, showPreview])

    function zoneSubmit(){
        let oldPayload;
        handleSubmitActions("save-basic-btn", "load-basic-btn");
        oldPayload = [{}];
        ApiRequester({reqEndpoint: "mytransaction", TransType: "zone_edit", Payload: backendData, OldPayload: oldPayload,
                        CampusId: props.campusId, User: dData.user, ClientId: dData.sessionId}).then(data => {
            if (data && data[0]){
                props.setCount(0);
                formSuccess("Zone information saved.", "save-basic-btn", "load-basic-btn");
            }
            else{
                formFail(`Error during save, error code: ${data && data?.length > 1 ? data[1] : ""}`,
                    "save-basic-btn", "load-basic-btn");
            }
        });
    }

    // Handle zone type change for a zone
    const handleZoneTypeChange = (featureId, newZoneTypeId) => {
        const typeValue = parseInt(newZoneTypeId);
        setZoneTypes(prev => ({
            ...prev,
            [featureId]: typeValue
        }));

        // Update feature properties
        const feature = draw.current.get(featureId);
        if (feature) {
            feature.properties.zone_type_id = typeValue;
            draw.current.add(feature);
        }

        if (showPreview) {
            setLoadData(true);
        }
    };

    // Handle layer change for a zone
    const handleLayerChange = (featureId, newLayer) => {
        const layerValue = parseInt(newLayer) || 0;
        setZoneLayers(prev => ({
            ...prev,
            [featureId]: layerValue
        }));

        // Update feature properties
        const feature = draw.current.get(featureId);
        if (feature) {
            feature.properties.layer = layerValue;
            draw.current.add(feature);
        }

        if (showPreview) {
            setLoadData(true);
        }
    };

    // Load existing zones when component mounts or zoneData changes
    useEffect(() => {
        if (map.current && draw.current && props.zoneData && props.zoneData.length > 0) {
            // Clear any existing features
            const existingFeatures = draw.current.getAll();
            existingFeatures.features.forEach(feature => {
                draw.current.delete(feature.id);
            });

            // Initialize zone names, layers, and types from the input data
            const initialZoneNames = {};
            const initialZoneLayers = {};
            const initialZoneTypes = {};

            // Process each zone from zoneData
            props.zoneData.forEach((zone, index) => {
                if (!zone || !zone.points || !Array.isArray(zone.points)) {
                    console.error(`Invalid zone data at index ${index}:`, zone);
                    return;
                }

                try {
                    // Create coordinates array from zone points
                    const coordinates = zone.points.map(point => {
                        if (!point || typeof point.longitude !== 'number' || typeof point.latitude !== 'number') {
                            console.error(`Invalid point data in zone ${index}:`, point);
                            return null;
                        }
                        return [point.longitude, point.latitude];
                    }).filter(coord => coord !== null);

                    if (coordinates.length < 3) {
                        console.error(`Zone ${index} has insufficient points:`, coordinates);
                        return;
                    }

                    // Close the polygon by adding the first point at the end
                    coordinates.push(coordinates[0]);

                    // Create feature with zone data in properties
                    const feature = {
                        type: "Feature",
                        properties: {
                            shape: "square",
                            zone_type_id: zone.zone_type_id,
                            zone_name: zone.zone_name,
                            layer: zone.layer,
                            zone_id: zone.zone_id
                        },
                        geometry: {
                            type: "Polygon",
                            coordinates: [coordinates]
                        }
                    };

                    // Add feature to map
                    draw.current.add(feature);
                } catch (error) {
                    console.error(`Error processing zone ${index}:`, error);
                }
            });

            // Get all features and set their data
            const allFeatures = draw.current.getAll();
            allFeatures.features.forEach(feature => {
                if (feature.properties) {
                    initialZoneNames[feature.id] = feature.properties.zone_name;
                    initialZoneLayers[feature.id] = feature.properties.layer;
                    initialZoneTypes[feature.id] = feature.properties.zone_type_id;
                }
            });

            // Set all zone data at once
            setZoneNames(initialZoneNames);
            setZoneLayers(initialZoneLayers);
            setZoneTypes(initialZoneTypes);

            // Update features and trigger data preview update
            updateFeatures();
            setLoadData(true);
        }
    }, [props.zoneData, mapInitialized]);

    return (
        <div className={"row"} style={{minHeight: "650px", height: "fit-content"}}>
            <div className={"col-3 sidebar p-4"} style={{minHeight: "650px", height: "fit-content"}}>
                <h1 className={"border-bottom border-secondary"}>PalCare Zone Designer</h1>
                {showHelp ? (
                    <div className="help-panel">
                        <h3>How to Use</h3>
                        <p>
                            <strong>Adding Squares:</strong> Use the "Add Square" button to
                            add a zone.
                        </p>
                        <p>
                            <strong>Editing:</strong> Select a zone to modify its shape with
                            the vertex handles.
                        </p>
                        <p>
                            <strong>Rotation:</strong> Use the rotate buttons when a zone is
                            selected.
                        </p>
                        <p>
                            <strong>Exporting:</strong> Use the export options to save or copy
                            your zones with hierarchy information.
                        </p>
                        <button className={"btn btn-primary"} onClick={() => {
                            setShowHelp(false);
                        }}>Close Help</button>
                    </div>
                ) : (
                    <>
                        {/*<div className={"map-info mb-3"}>*/}
                        {/*    <div>*/}
                        {/*        Longitude: {lng} | Latitude: {lat} | Zoom: {zoom}*/}
                        {/*    </div>*/}
                        {/*</div>*/}

                        <div className={"address-search mb-3"}>
                            <h3 className={"mb-1"}>Search Location</h3>
                            <div className="search-container">
                                <input type="text" placeholder="Enter address" value={address}
                                       onChange={(e) => setAddress(e.target.value)}
                                       onKeyDown={(e) => e.key === "Enter" && geocodeAddress()} />
                                <button className={"btn btn-primary"} onClick={() => {
                                    geocodeAddress();
                                    if (showPreview){
                                        setLoadData(true);
                                    }
                                }}>Search</button>
                            </div>
                            {searchError && (
                                <div className="error-message">{searchError}</div>
                            )}
                        </div>

                        <div className={"controls mb-3"}>
                            <h3 className={"mb-1"}>Drawing Tools</h3>
                            <div className="button-group">
                                <button className={"btn btn-primary mb-3"} onClick={() => {
                                    addSquare();
                                    if (showPreview){
                                        setLoadData(true);
                                    }
                                }} disabled={!mapInitialized}>
                                    Add Square
                                </button>
                            </div>
                        </div>

                        {/* Zone Type Selection for New Zones */}
                        <div className="zone-type-selection mb-3">
                            <h3 className="mb-1">Default Zone Type for New Zones</h3>
                            <select 
                                id="zoneTypeSelect" 
                                className="form-select"
                                value={selectedZoneType}
                                onChange={(e) => {
                                    setSelectedZoneType(parseInt(e.target.value));
                                    if (showPreview) {
                                        setLoadData(true);
                                    }
                                }}
                            >
                                {props.zoneTypes && props.zoneTypes.map((type) => (
                                    <option key={type.zone_type_id} value={type.zone_type_id}>
                                        {type.zone_type_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {features.length > 0 && (
                            <div className={"feature-controls mb-3"}>
                                <h3 className={"mb-1"}>Edit Selected Zone</h3>
                                <div className="button-group">
                                    <button className={"btn btn-primary"} onClick={() => rotateFeature(-15)} disabled={!activeFeatureId}>
                                        Rotate ↺ 15°
                                    </button>
                                    <button className={"btn btn-primary"} onClick={() => rotateFeature(15)} disabled={!activeFeatureId}>
                                        Rotate ↻ 15°
                                    </button>
                                    <button className={"btn btn-primary"} onClick={() => {
                                        duplicateFeature();
                                        if (showPreview){
                                            setLoadData(true);
                                        }
                                    }} disabled={!activeFeatureId}>
                                        Duplicate
                                    </button>
                                    <button className={"btn btn-danger"} disabled={!activeFeatureId}
                                            onClick={() => {
                                                if (draw.current && activeFeatureId) {
                                                    draw.current.delete(activeFeatureId);
                                                    updateFeatures();
                                                }
                                                if (showPreview) {
                                                    setLoadData(true);
                                                }
                                            }}>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Conditionally render Zone Manager only when zones exist */}
                        {features.length > 0 && (
                            <div className={"zone-manager mb-3"}>
                                <h3 className={"mb-1"}>Zone Manager</h3>
                                <div className="zone-list">
                                    {features.map((feature, index) => {
                                        const {displayName} = getZoneDisplayInfo(feature.id, index);
                                        return (
                                            <div key={feature.id}
                                                 className={`zone-item ${feature.id === activeFeatureId ? "active" : ""}`}
                                                 onClick={() => handleZoneItemClick(feature.id)}>
                                                {editingZoneId === feature.id ? (
                                                    <input type="text"
                                                           className="zone-name-input"
                                                           value={editingText}
                                                           onChange={(e) => setEditingText(e.target.value)}
                                                           onBlur={() => {
                                                               handleSaveZoneName(feature.id);
                                                               if (showPreview){
                                                                    setLoadData(true);
                                                                }
                                                           }}
                                                           onKeyDown={(e) => {
                                                               if (e.key === "Enter") {
                                                                   handleSaveZoneName(feature.id);
                                                                   if (showPreview){
                                                                        setLoadData(true);
                                                                    }
                                                               }
                                                           }}
                                                           autoFocus onClick={(e) => e.stopPropagation()}/>
                                                ) : (
                                                    <>
                                                        <div className="zone-info">
                                                            <span className="zone-name">{displayName}</span>
                                                            <div className="zone-controls">
                                                                <div className="zone-layer-input">
                                                                    <label>Layer:</label>
                                                                    <input 
                                                                        type="number" 
                                                                        min="0"
                                                                        value={zoneLayers[feature.id] || 0}
                                                                        onChange={(e) => {
                                                                            e.stopPropagation();
                                                                            handleLayerChange(feature.id, e.target.value);
                                                                        }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                </div>
                                                                <div className="zone-type-input">
                                                                    <label>Type:</label>
                                                                    <select
                                                                        value={zoneTypes[feature.id] || selectedZoneType}
                                                                        onChange={(e) => {
                                                                            e.stopPropagation();
                                                                            handleZoneTypeChange(feature.id, e.target.value);
                                                                        }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        {props.zoneTypes && props.zoneTypes.map((type) => (
                                                                            <option key={type.zone_type_id} value={type.zone_type_id}>
                                                                                {type.zone_type_name}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="zone-actions">
                                                            <button
                                                                className="edit-button"
                                                                title="Edit zone name"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    startEditing(feature.id);
                                                                }}>
                                                                <EditIcon/>
                                                            </button>
                                                            <button className="delete-button" title="Delete zone"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (draw.current) {
                                                                            draw.current.delete(feature.id);
                                                                            updateFeatures();
                                                                        }
                                                                        if (showPreview){
                                                                            setLoadData(true);
                                                                        }
                                                                    }}>×
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className={"save-options mb-3"}>
                            <h3 className={"mb-1"}>Save Options</h3>
                            <div className="button-group">
                                <Button text={"Save Zones"} class={"btn btn-primary float-end me-3"}
                                                id={"save-basic-btn"}
                                                disabled={!mapInitialized || features.length === 0} onClick={() => {
                                                    zoneSubmit()
                                        }}/>
                                <Button type={"button"} class={"btn btn-primary float-end mx-3 d-none"}
                                                disabled={true}
                                                spinner={true} text={""} id={"load-basic-btn"}/>
                            </div>
                        </div>

                        <div className={"export mb-3"}>
                            <h3 className={"mb-1"}>Export Options</h3>
                            <div className="button-group">
                                <button className={"btn btn-secondary fs-5 py-3"} onClick={exportJson} disabled={!mapInitialized || features.length === 0}>
                                    Download JSON
                                </button>
                                <button className={"btn btn-secondary fs-5 py-3"} onClick={exportCsv} disabled={!mapInitialized || features.length === 0}>
                                    Download CSV
                                </button>
                            </div>
                        </div>

                        {/* Conditionally render the data preview only when zones exist */}
                        {showPreview && (
                            <div className={"backend-preview mt-0"}>
                                <h3 className={"mb-1"}>Zone Data Preview</h3>
                                <pre className="data-display">
                                    {JSON.stringify(backendData, null, 2)}
                                </pre>
                            </div>
                        )}
                        <div className="help-button">
                            <button className={"btn btn-light text-light"} onClick={() => setShowHelp(true)}>Show Help
                            </button>
                            {!showPreview &&
                                <button className={"btn btn-light text-light"}
                                        onClick={() => setShowPreview(true)}>
                                    Show preview
                                </button>
                            }
                            {showPreview &&
                                <button className={"btn btn-light text-light"}
                                        onClick={() => setShowPreview(false)}>
                                    Hide preview
                                </button>
                            }
                        </div>
                    </>
                )}
            </div>
            <div ref={mapContainer} className={"map-container col-9"} style={{height: "auto", zIndex: 0}}/>
        </div>
    )
}

export default GeofenceTool;